import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../Contexts/AuthContext';

type ChaletType = 0 | 1; // Big=0, Small=1

const AdminDiagnostics: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const isAdmin = useMemo(() => {
    if (!user) return false;
    const roleClaim = user["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"]; // string or array depending on token
    if (Array.isArray(roleClaim)) {
      return roleClaim.includes('Admin') || roleClaim.includes('SuperAdmin');
    }
    return roleClaim === 'Admin' || roleClaim === 'SuperAdmin';
  }, [user]);

  const [pathsResult, setPathsResult] = useState<any>(null);
  const [pathsError, setPathsError] = useState<string>('');
  const [pathsLoading, setPathsLoading] = useState<boolean>(false);
  const [pathsResolvedUrl, setPathsResolvedUrl] = useState<string>('');

  const [chaletType, setChaletType] = useState<ChaletType>(0);
  const [from, setFrom] = useState<string>(''); // yyyy-MM-dd
  const [to, setTo] = useState<string>(''); // yyyy-MM-dd
  const [availResult, setAvailResult] = useState<any>(null);
  const [availError, setAvailError] = useState<string>('');
  const [availLoading, setAvailLoading] = useState<boolean>(false);
  const [availResolvedUrl, setAvailResolvedUrl] = useState<string>('');
  
  // Order diagnostics state
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [orderDiagLoading, setOrderDiagLoading] = useState<boolean>(false);
  const [orderDiagError, setOrderDiagError] = useState<string>('');
  const [orderDiagResult, setOrderDiagResult] = useState<any>(null);
  const [orderDiagResolvedUrl, setOrderDiagResolvedUrl] = useState<string>('');

  // Repair booking dates state
  const [repairBookingId, setRepairBookingId] = useState<string>(''); // optional numeric
  const [repairOrderNumber, setRepairOrderNumber] = useState<string>('');
  const [repairFrom, setRepairFrom] = useState<string>(''); // yyyy-MM-dd
  const [repairTo, setRepairTo] = useState<string>(''); // yyyy-MM-dd
  const [repairPackageId, setRepairPackageId] = useState<string>(''); // optional numeric
  const [repairAddDays, setRepairAddDays] = useState<number>(1);
  const [repairAddDaysArrival, setRepairAddDaysArrival] = useState<string>(''); // optional numeric (can be negative/zero)
  const [repairAddDaysDeparture, setRepairAddDaysDeparture] = useState<string>(''); // optional numeric (can be negative/zero)
  const [repairMax, setRepairMax] = useState<number>(200);
  const [repairLoading, setRepairLoading] = useState<boolean>(false);
  const [repairError, setRepairError] = useState<string>('');
  const [repairResult, setRepairResult] = useState<any>(null);
  const [repairResolvedUrl, setRepairResolvedUrl] = useState<string>('');
  
  // Pricing Model ChaletType Check state
  const [pricingCheckPackageId, setPricingCheckPackageId] = useState<string>('');
  const [pricingCheckLoading, setPricingCheckLoading] = useState<boolean>(false);
  const [pricingCheckError, setPricingCheckError] = useState<string>('');
  const [pricingCheckResult, setPricingCheckResult] = useState<any>(null);
  const [pricingCheckResolvedUrl, setPricingCheckResolvedUrl] = useState<string>('');
  
  const diagnosticBase = 'Diagnostic';

  if (!isAuthenticated) {
    return <div className="container"><div className="alert alert-warning mt-3">Please log in to access diagnostics.</div></div>;
  }

  if (!isAdmin) {
    return <div className="container"><div className="alert alert-danger mt-3">Admin only: you don\'t have permission to view this page.</div></div>;
  }

  const handleFetchPaths = async () => {
    setPathsLoading(true);
    setPathsError('');
    setPathsResult(null);
    setPathsResolvedUrl('');
    try {
      const url = `${apiUrl}/${diagnosticBase}/paths`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      setPathsResult(res.data);
      setPathsResolvedUrl(url);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err.message || 'Request failed';
      const tried = err?.triedUrl ? ` (${err.triedUrl})` : '';
      setPathsError(`${message}${status ? ` [${status}]` : ''}${tried}`);
    } finally {
      setPathsLoading(false);
    }
  };

  const handleFetchAvailability = async () => {
    setAvailLoading(true);
    setAvailError('');
    setAvailResult(null);
    setAvailResolvedUrl('');
    if (!from || !to) {
      setAvailError('Please select From and To dates (yyyy-MM-dd).');
      setAvailLoading(false);
      return;
    }
    try {
      const url = `${apiUrl}/${diagnosticBase}/availability-debug`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        params: { chaletType, from, to },
      });
      setAvailResult(res.data);
      setAvailResolvedUrl(url);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err.message || 'Request failed';
      const tried = err?.triedUrl ? ` (${err.triedUrl})` : '';
      setAvailError(`${message}${status ? ` [${status}]` : ''}${tried}`);
    } finally {
      setAvailLoading(false);
    }
  };

  const handleFetchOrderDiagnostics = async () => {
    setOrderDiagLoading(true);
    setOrderDiagError('');
    setOrderDiagResult(null);
    setOrderDiagResolvedUrl('');
    if (!orderNumber.trim()) {
      setOrderDiagError('Please enter an order number.');
      setOrderDiagLoading(false);
      return;
    }
    try {
      const url = `${apiUrl}/${diagnosticBase}/order-diagnostics/${encodeURIComponent(orderNumber.trim())}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      setOrderDiagResult(res.data);
      setOrderDiagResolvedUrl(url);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err.message || 'Request failed';
      setOrderDiagError(`${message}${status ? ` [${status}]` : ''}`);
    } finally {
      setOrderDiagLoading(false);
    }
  };

  const runRepair = async (commit: boolean) => {
    setRepairLoading(true);
    setRepairError('');
    setRepairResult(null);
    setRepairResolvedUrl('');

    // Basic validation mirroring server expectations
    const hasOrder = !!repairOrderNumber.trim();
    const hasWindow = !!repairFrom && !!repairTo;
    const pkgNum = repairPackageId.trim() ? Number(repairPackageId.trim()) : NaN;
    const hasPackage = Number.isInteger(pkgNum) && pkgNum > 0;
    const bookingNum = repairBookingId.trim() ? Number(repairBookingId.trim()) : NaN;
    const hasBooking = Number.isInteger(bookingNum) && bookingNum > 0;
    if (!hasBooking && !hasOrder && !hasWindow && !hasPackage) {
      setRepairError('Provide a Booking ID OR an order number OR both From/To dates OR a Package ID.');
      setRepairLoading(false);
      return;
    }
    if (hasWindow && repairTo < repairFrom) {
      setRepairError('Invalid date window: To must be >= From.');
      setRepairLoading(false);
      return;
    }

    if (commit) {
      const ok = window.confirm(`This will shift stored booking dates by +${repairAddDays} days for the selected records. Proceed?`);
      if (!ok) {
        setRepairLoading(false);
        return;
      }
    }

    try {
      const primaryBase = diagnosticBase;
      const altBase = primaryBase === 'Diagnostic' ? 'Diagnostics' : 'Diagnostic';
      const params: any = {
        addDays: repairAddDays,
        commit,
        max: repairMax,
      };
      if (hasBooking) params.bookingId = bookingNum;
      if (hasOrder) params.orderNumber = repairOrderNumber.trim();
      if (hasWindow) {
        params.from = repairFrom; // yyyy-MM-dd from input
        params.to = repairTo;
      }
      if (hasPackage) {
        params.packageId = pkgNum;
      }
      // Optional arrival/departure specific deltas
      const aDelta = repairAddDaysArrival.trim();
      const dDelta = repairAddDaysDeparture.trim();
      if (aDelta !== '' && !Number.isNaN(Number(aDelta))) params.addDaysArrival = Number(aDelta);
      if (dDelta !== '' && !Number.isNaN(Number(dDelta))) params.addDaysDeparture = Number(dDelta);
      const primaryUrl = `${apiUrl}/${primaryBase}/repair-booking-dates`;
      const altUrl = `${apiUrl}/${altBase}/repair-booking-dates`;

      const exec = async (url: string) => axios.post(url, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        params,
      });

      try {
        const res = await exec(primaryUrl);
        setRepairResult(res.data);
        setRepairResolvedUrl(primaryUrl + `?` + new URLSearchParams(params).toString());
      } catch (err1: any) {
        const status1 = err1?.response?.status;
        if (status1 === 404 || status1 === 405) {
          // Retry with alternate base (Diagnostic <-> Diagnostics)
          const res2 = await exec(altUrl);
          setRepairResult(res2.data);
          setRepairResolvedUrl(altUrl + `?` + new URLSearchParams(params).toString());
        } else {
          throw err1;
        }
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err.message || 'Request failed';
      setRepairError(`${message}${status ? ` [${status}]` : ''}`);
    } finally {
      setRepairLoading(false);
    }
  };

  const handleFetchPricingModelCheck = async () => {
    setPricingCheckLoading(true);
    setPricingCheckError('');
    setPricingCheckResult(null);
    setPricingCheckResolvedUrl('');
    try {
      const params: any = {};
      const pkgNum = pricingCheckPackageId.trim() ? Number(pricingCheckPackageId.trim()) : NaN;
      if (Number.isInteger(pkgNum) && pkgNum > 0) {
        params.packageId = pkgNum;
      }
      const url = `${apiUrl}/${diagnosticBase}/pricing-model-chalettype-check`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        params,
      });
      setPricingCheckResult(res.data);
      const queryStr = Object.keys(params).length > 0 ? `?${new URLSearchParams(params).toString()}` : '';
      setPricingCheckResolvedUrl(url + queryStr);
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err.message || 'Request failed';
      setPricingCheckError(`${message}${status ? ` [${status}]` : ''}`);
    } finally {
      setPricingCheckLoading(false);
    }
  };

  return (
    <div className="container py-4 mb-4">
      <h2>Diagnostics</h2>

      <section className="mt-4">
        <h4>Paths</h4>
        <button className="btn btn-primary mb-2" onClick={handleFetchPaths} disabled={pathsLoading}>
          {pathsLoading ? 'Loading…' : 'Fetch Paths'}
        </button>
        {pathsError && <div className="alert alert-danger">{pathsError}</div>}
        {pathsResolvedUrl && (
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>Resolved URL: {pathsResolvedUrl}</div>
        )}
        {pathsResult && (
          <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
            {JSON.stringify(pathsResult, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      <section className="mt-4">
        <h4>Availability Debug (Admin)</h4>
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label htmlFor="chaletType" className="form-label">Chalet Type</label>
            <select id="chaletType" className="form-select" value={chaletType}
              onChange={(e) => setChaletType(Number(e.target.value) as ChaletType)}>
              <option value={0}>Big (0)</option>
              <option value={1}>Small (1)</option>
            </select>
          </div>
          <div className="col-md-3">
            <label htmlFor="fromDate" className="form-label">From (yyyy-MM-dd)</label>
            <input id="fromDate" className="form-control" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label htmlFor="toDate" className="form-label">To (yyyy-MM-dd)</label>
            <input id="toDate" className="form-control" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100" onClick={handleFetchAvailability} disabled={availLoading}>
              {availLoading ? 'Loading…' : 'Fetch Availability'}
            </button>
          </div>
        </div>
        {availError && <div className="alert alert-danger mt-2">{availError}</div>}
        {availResolvedUrl && (
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>Resolved URL: {availResolvedUrl}</div>
        )}
        {availResult && (
          <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: 4, maxHeight: 400, overflow: 'auto' }}>
            {JSON.stringify(availResult, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      <section className="mt-4">
        <h4>Order Diagnostics (Admin)</h4>
        <div className="row g-2 align-items-end">
          <div className="col-md-6">
            <label htmlFor="orderNumber" className="form-label">Order Number</label>
            <input id="orderNumber" className="form-control" type="text" value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)} placeholder="e.g. ORD-12345" />
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100 mt-4 mt-md-0" onClick={handleFetchOrderDiagnostics} disabled={orderDiagLoading}>
              {orderDiagLoading ? 'Loading…' : 'Diagnose Order'}
            </button>
          </div>
        </div>
        {orderDiagError && <div className="alert alert-danger mt-2">{orderDiagError}</div>}
        {orderDiagResolvedUrl && (
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>Resolved URL: {orderDiagResolvedUrl}</div>
        )}
        {orderDiagResult && (
          <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: 4, maxHeight: 500, overflow: 'auto' }}>
            {JSON.stringify(orderDiagResult, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      <section className="mt-4 mb-4">
        <h4>Repair Booking Dates (Admin)</h4>
        <p className="text-muted" style={{ marginTop: -6 }}>Shift stored arrival/departure by +N days. Provide a Booking ID, an Order Number, a From/To window, or a Package ID. You can override Arrival and/or Departure deltas. Use Preview first; Apply will persist changes.</p>
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label htmlFor="repairBookingId" className="form-label">Booking ID (optional)</label>
            <input id="repairBookingId" className="form-control" type="number" value={repairBookingId}
              onChange={(e) => setRepairBookingId(e.target.value)} placeholder="e.g. 123" />
          </div>
          <div className="col-md-3">
            <label htmlFor="repairOrder" className="form-label">Order Number (optional)</label>
            <input id="repairOrder" className="form-control" type="text" value={repairOrderNumber}
              onChange={(e) => setRepairOrderNumber(e.target.value)} placeholder="e.g. ORD-12345" />
          </div>
          <div className="col-md-3">
            <label htmlFor="repairFrom" className="form-label">From (yyyy-MM-dd)</label>
            <input id="repairFrom" className="form-control" type="date" value={repairFrom}
              onChange={(e) => setRepairFrom(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label htmlFor="repairTo" className="form-label">To (yyyy-MM-dd)</label>
            <input id="repairTo" className="form-control" type="date" value={repairTo}
              onChange={(e) => setRepairTo(e.target.value)} />
          </div>
          <div className="col-md-2 d-none">
            <label htmlFor="repairAdd" className="form-label">Add Days</label>
            <input id="repairAdd" className="form-control" type="number" value={repairAddDays}
              onChange={(e) => setRepairAddDays(Number(e.target.value) || 0)} />
          </div>
        </div>
        <div className="row g-2 align-items-end mt-2">
          <div className="col-md-2">
            <label htmlFor="repairPackageId" className="form-label">Package ID (optional)</label>
            <input id="repairPackageId" className="form-control" type="number" value={repairPackageId}
              onChange={(e) => setRepairPackageId(e.target.value)} placeholder="e.g. 1" />
          </div>
          <div className="col-md-2">
            <label htmlFor="repairAddArr" className="form-label">Arrival Δ days (optional)</label>
            <input id="repairAddArr" className="form-control" type="number" value={repairAddDaysArrival}
              onChange={(e) => setRepairAddDaysArrival(e.target.value)} placeholder="e.g. 0 or 1 or -1" />
          </div>
          <div className="col-md-2">
            <label htmlFor="repairAddDep" className="form-label">Departure Δ days (optional)</label>
            <input id="repairAddDep" className="form-control" type="number" value={repairAddDaysDeparture}
              onChange={(e) => setRepairAddDaysDeparture(e.target.value)} placeholder="e.g. 1" />
          </div>
          <div className="col-md-2">
            <label htmlFor="repairMax" className="form-label">Max</label>
            <input id="repairMax" className="form-control" type="number" value={repairMax}
              onChange={(e) => setRepairMax(Math.max(1, Number(e.target.value) || 1))} />
          </div>
          <div className="col-md-3">
            <button className="btn btn-outline-secondary w-100 mt-4 mt-md-0"
              onClick={() => runRepair(false)} disabled={repairLoading}>
              {repairLoading ? 'Working…' : 'Preview (no commit)'}
            </button>
          </div>
          <div className="col-md-3 mb-16">
            <button className="btn btn-danger w-100 mt-4 mt-md-0"
              onClick={() => runRepair(true)} disabled={repairLoading}>
              {repairLoading ? 'Working…' : 'Apply (commit)'}
            </button>
          </div>
        </div>
        {repairError && <div className="alert alert-danger mt-2">{repairError}</div>}
        {repairResolvedUrl && (
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>Resolved URL: {repairResolvedUrl}</div>
        )}
        {repairResult && (
          <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: 4, maxHeight: 500, overflow: 'auto' }}>
            {JSON.stringify(repairResult, null, 2)}
          </pre>
        )}
      </section>

      <hr />

      <section className="mt-4 mb-4">
        <h4>Pricing Model ChaletType Check (Raw SQL)</h4>
        <p className="text-muted" style={{ marginTop: -6 }}>Raw SQL query to check PricingModel ChaletType values, bypassing EF cache. Useful for debugging availability issues per package.</p>
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label htmlFor="pricingCheckPackageId" className="form-label">Package ID (optional)</label>
            <input id="pricingCheckPackageId" className="form-control" type="number" value={pricingCheckPackageId}
              onChange={(e) => setPricingCheckPackageId(e.target.value)} placeholder="Leave empty for all packages" />
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100 mt-4 mt-md-0" onClick={handleFetchPricingModelCheck} disabled={pricingCheckLoading}>
              {pricingCheckLoading ? 'Loading…' : 'Check ChaletType Values'}
            </button>
          </div>
        </div>
        {pricingCheckError && <div className="alert alert-danger mt-2">{pricingCheckError}</div>}
        {pricingCheckResolvedUrl && (
          <div className="text-muted" style={{ fontSize: '0.9rem' }}>Resolved URL: {pricingCheckResolvedUrl}</div>
        )}
        {pricingCheckResult && (
          <div className="mt-3">
            <div className="mb-2">
              <strong>Message:</strong> {pricingCheckResult.message}<br />
              <strong>Package Filter:</strong> {pricingCheckResult.packageIdFilter ?? 'None (all packages)'}<br />
              <strong>Count:</strong> {pricingCheckResult.count}
            </div>
            {pricingCheckResult.pricingModels && pricingCheckResult.pricingModels.length > 0 && (
              <table className="table table-sm table-striped table-bordered">
                <thead>
                  <tr>
                    <th>PricingModel ID</th>
                    <th>Package ID</th>
                    <th>Package Name</th>
                    <th>Description</th>
                    <th>ChaletType (raw)</th>
                    <th>ChaletType (string)</th>
                  </tr>
                </thead>
                <tbody>
                  {pricingCheckResult.pricingModels.map((pm: any) => (
                    <tr key={pm.pricingModelId} style={{
                      backgroundColor: pm.chaletType === null ? '#fff3cd' : (pm.chaletType === 0 ? '#d4edda' : '#f8d7da')
                    }}>
                      <td>{pm.pricingModelId}</td>
                      <td>{pm.packageId}</td>
                      <td>{pm.packageName ?? '-'}</td>
                      <td>{pm.description ?? '-'}</td>
                      <td><code>{pm.chaletType === null ? 'NULL' : pm.chaletType}</code></td>
                      <td>{pm.chaletTypeString}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <pre style={{ background: '#f8f9fa', padding: '1rem', borderRadius: 4, maxHeight: 300, overflow: 'auto' }}>
              {JSON.stringify(pricingCheckResult, null, 2)}
            </pre>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminDiagnostics;
